import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Printer } from "lucide-react";
import { api } from "../lib/api";
import { tk, bn, fmtDate, fmtTime } from "../lib/format";
import { useSettings } from "../context/SettingsContext";
import { Loader } from "../components/kit";

function Cell({ label, value, strong }: { label: string; value: string; strong?: boolean }) {
  return (
    <div className="border border-slate-200 rounded-lg px-4 py-3">
      <p className="text-xs text-slate-500">{label}</p>
      <p className={`num mt-0.5 ${strong ? "text-xl font-bold text-slate-900" : "text-lg font-semibold text-slate-800"}`}>{value}</p>
    </div>
  );
}

export default function DayCloseReport() {
  const { id } = useParams();
  const { settings } = useSettings();
  const SHOP = settings.shop;
  const [r, setR] = useState<any>(null);
  useEffect(() => { api.get(`/day-close/${id}`).then((res) => setR(res.data)); }, [id]);
  if (!r) return <Loader />;

  return (
    <div data-testid="day-close-report-page">
      <div className="no-print flex flex-wrap items-center justify-between gap-2 mb-4">
        <Link to="/reports" className="btn btn-ghost btn-sm"><ArrowLeft size={16} /> রিপোর্ট তালিকা</Link>
        <button className="btn btn-primary btn-sm" onClick={() => window.print()} data-testid="report-print-button"><Printer size={16} /> প্রিন্ট / PDF ডাউনলোড</button>
      </div>

      <div className="print-area bg-white text-slate-900 max-w-[800px] mx-auto rounded-xl shadow-xl p-8" style={{ fontFamily: "'Hind Siliguri', sans-serif" }}>
        <div className="flex items-center justify-between border-b-2 border-slate-800 pb-3">
          <div>
            <h1 className="text-2xl font-extrabold text-slate-900">{SHOP.name}</h1>
            <p className="text-sm text-slate-500">{SHOP.address}</p>
          </div>
          <div className="text-right">
            <p className="text-lg font-bold text-emerald-700">দিন ক্লোজ রিপোর্ট</p>
            <p className="text-sm num text-slate-600">নং: {r.report_no}</p>
            <p className="text-sm num text-slate-600">তারিখ: {fmtDate(r.date)}</p>
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-5" data-testid="report-summary">
          <Cell label="মোট বিক্রি" value={tk(r.total_sale)} strong />
          <Cell label="বিক্রিতে পরিশোধ" value={tk(r.total_paid)} />
          <Cell label="আজকের নতুন বাকি" value={tk(r.total_due)} />
          <Cell label="বাকি আদায়" value={tk(r.total_collection)} />
          <Cell label="মোট ক্যাশ এসেছে" value={tk(r.cash_in)} />
          <Cell label="দোকান খরচ" value={tk(r.total_expense || 0)} />
          <Cell label="নিট ক্যাশ (হাতে)" value={tk(r.net_cash ?? r.cash_in)} strong />
          <Cell label="মোট ক্রয়" value={tk(r.purchase_total)} />
          <Cell label="মেমো সংখ্যা" value={bn(r.memo_count)} />
          <Cell label="আদায় সংখ্যা" value={bn(r.payment_count)} />
          <Cell label="ক্রয় এন্ট্রি" value={bn(r.purchase_count)} />
          <Cell label="নতুন ক্রেতা" value={bn(r.new_customer_count)} />
          <Cell label="বাকিতে বিক্রি (মেমো)" value={bn(r.due_customer_count)} />
        </div>

        {r.memos?.length > 0 && (
          <div className="mt-6">
            <h2 className="font-bold text-slate-800 mb-2">মেমো তালিকা</h2>
            <table className="w-full text-sm border border-slate-200">
              <thead><tr className="bg-slate-100 text-left"><th className="px-3 py-2">সময়</th><th className="px-3 py-2">মেমো</th><th className="px-3 py-2">ক্রেতা</th><th className="px-3 py-2 text-right">মোট</th><th className="px-3 py-2 text-right">পরিশোধ</th><th className="px-3 py-2 text-right">বাকি</th></tr></thead>
              <tbody>
                {r.memos.map((m: any) => (
                  <tr key={m.id} className="border-t border-slate-100">
                    <td className="px-3 py-1.5 num">{fmtTime(m.created_at)}</td>
                    <td className="px-3 py-1.5 font-semibold">{m.invoice_no}</td>
                    <td className="px-3 py-1.5">{m.customer?.name}</td>
                    <td className="px-3 py-1.5 text-right num">{tk(m.grand_total)}</td>
                    <td className="px-3 py-1.5 text-right num">{tk(m.paid)}</td>
                    <td className="px-3 py-1.5 text-right num">{tk(m.current_due)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {r.payments?.length > 0 && (
          <div className="mt-6">
            <h2 className="font-bold text-slate-800 mb-2">আদায় তালিকা</h2>
            <table className="w-full text-sm border border-slate-200">
              <thead><tr className="bg-slate-100 text-left"><th className="px-3 py-2">রশিদ</th><th className="px-3 py-2">ক্রেতা</th><th className="px-3 py-2">মাধ্যম</th><th className="px-3 py-2 text-right">আদায়</th></tr></thead>
              <tbody>
                {r.payments.map((p: any) => (
                  <tr key={p.id} className="border-t border-slate-100"><td className="px-3 py-1.5 font-semibold">{p.receipt_no}</td><td className="px-3 py-1.5">{p.customer_name}</td><td className="px-3 py-1.5">{p.method}</td><td className="px-3 py-1.5 text-right num">{tk(p.amount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {r.expenses?.length > 0 && (
          <div className="mt-6">
            <h2 className="font-bold text-slate-800 mb-2">খরচের তালিকা</h2>
            <table className="w-full text-sm border border-slate-200">
              <thead><tr className="bg-slate-100 text-left"><th className="px-3 py-2">খাত</th><th className="px-3 py-2">নোট</th><th className="px-3 py-2 text-right">পরিমাণ</th></tr></thead>
              <tbody>
                {r.expenses.map((e: any) => (
                  <tr key={e.id} className="border-t border-slate-100"><td className="px-3 py-1.5 font-semibold">{e.category}</td><td className="px-3 py-1.5">{e.note || "—"}</td><td className="px-3 py-1.5 text-right num text-red-600">{tk(e.amount)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <p className="text-xs text-slate-400 mt-8 text-center">ক্লোজ করেছেন: {r.closed_by} • {fmtDate(r.date)}</p>
      </div>
    </div>
  );
}
